import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@repo/ui/components/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/components/dialog';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Separator } from '@repo/ui/components/separator';
import { Switch } from '@repo/ui/components/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/components/tabs';
import {
  IconCashBanknote,
  IconMinus,
  IconMoon,
  IconPlus,
  IconPrinter,
  IconReceipt,
  IconRefresh,
  IconSearch,
  IconTicket,
  IconWifiOff,
} from '@tabler/icons-react';
import { useState } from 'react';

// Mock only. Nothing here is wired to anything — it exists so the theme, the
// two size scales and the Base UI primitives can be looked at in one place
// before any real screen gets built.

const rupees = (paise: number) =>
  `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TICKETS = [
  { label: 'Adult', price: 6000 },
  { label: 'Child', price: 3000 },
  { label: 'Senior', price: 4000 },
  { label: 'Camera', price: 5000 },
  { label: 'Boating', price: 15000 },
  { label: 'Parking', price: 2000 },
];

const SALES = [
  { id: 'TKT-4821', outlet: 'Gate 1', mode: 'Cash', status: 'synced', total: 18000 },
  { id: 'TKT-4822', outlet: 'Gate 1', mode: 'UPI', status: 'synced', total: 6000 },
  { id: 'CAF-1190', outlet: 'Cafe', mode: 'UPI', status: 'pending', total: 42500 },
  { id: 'TKT-4823', outlet: 'Gate 2', mode: 'Card', status: 'synced', total: 12000 },
  { id: 'SHP-0332', outlet: 'Souvenirs', mode: 'Cash', status: 'failed', total: 95000 },
];

const STATUS_VARIANT = {
  synced: 'secondary',
  pending: 'outline',
  failed: 'destructive',
} as const;

export function Preview() {
  const [dark, setDark] = useState(false);
  const [cart, setCart] = useState<Record<string, number>>({ Adult: 2, Child: 1 });

  const toggleTheme = (checked: boolean) => {
    setDark(checked);
    document.documentElement.classList.toggle('dark', checked);
  };

  const adjust = (label: string, by: number) =>
    setCart((current) => {
      const next = Math.max(0, (current[label] ?? 0) + by);
      const { [label]: _removed, ...rest } = current;
      return next === 0 ? rest : { ...current, [label]: next };
    });

  const cartTotal = TICKETS.reduce(
    (sum, ticket) => sum + (cart[ticket.label] ?? 0) * ticket.price,
    0,
  );
  const cartCount = Object.values(cart).reduce((sum, quantity) => sum + quantity, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-3">
          <span className="text-lg font-semibold tracking-tight">
            Government Botanical Garden Munnar
          </span>
          <Badge variant="outline" className="gap-1.5">
            <IconWifiOff className="size-3.5" />
            Offline
          </Badge>
          <div className="flex-1" />
          <Label htmlFor="theme" className="gap-1.5 text-sm text-muted-foreground">
            <IconMoon className="size-4" />
            Dark
          </Label>
          <Switch id="theme" checked={dark} onCheckedChange={toggleTheme} />
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
        <Alert>
          <IconWifiOff />
          <AlertTitle>Working offline — 3 sales queued</AlertTitle>
          <AlertDescription>
            Sales are written locally and sent when the connection returns. Nothing is lost.
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="till">
          <TabsList>
            <TabsTrigger value="till">Till</TabsTrigger>
            <TabsTrigger value="office">Back office</TabsTrigger>
            <TabsTrigger value="palette">Palette</TabsTrigger>
          </TabsList>

          {/* The till: everything here is sized for a thumb, not a mouse. */}
          <TabsContent value="till" className="pt-6">
            <div className="grid items-start gap-6 lg:grid-cols-[1fr_22rem]">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {TICKETS.map((ticket) => (
                  <Card key={ticket.label} size="sm" className="gap-3 px-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold">{ticket.label}</div>
                        <div className="text-sm text-muted-foreground numeric">
                          {rupees(ticket.price)}
                        </div>
                      </div>
                      {(cart[ticket.label] ?? 0) > 0 && (
                        <Badge className="numeric">{cart[ticket.label]}</Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="icon-touch"
                        variant="outline"
                        aria-label={`Remove one ${ticket.label}`}
                        onClick={() => adjust(ticket.label, -1)}
                      >
                        <IconMinus />
                      </Button>
                      <Button
                        size="touch"
                        className="flex-1"
                        aria-label={`Add one ${ticket.label}`}
                        onClick={() => adjust(ticket.label, 1)}
                      >
                        <IconPlus />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>

              <Card className="h-fit">
                <CardHeader>
                  <CardTitle>Current sale</CardTitle>
                  <CardDescription>Gate 1 · Terminal 2</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {cartCount === 0 && (
                    <p className="text-sm text-muted-foreground">Nothing added yet.</p>
                  )}
                  {TICKETS.filter((ticket) => cart[ticket.label]).map((ticket) => (
                    <div key={ticket.label} className="flex items-baseline justify-between text-sm">
                      <span>
                        {ticket.label} <span className="text-muted-foreground">×</span>{' '}
                        <span className="numeric">{cart[ticket.label]}</span>
                      </span>
                      <span className="font-medium numeric">
                        {rupees((cart[ticket.label] ?? 0) * ticket.price)}
                      </span>
                    </div>
                  ))}
                  <Separator />
                  <div className="flex items-baseline justify-between">
                    <span className="font-medium">Total</span>
                    <span className="text-2xl font-semibold numeric">{rupees(cartTotal)}</span>
                  </div>
                </CardContent>
                <CardFooter className="flex-col gap-2">
                  <Button size="touch" className="w-full">
                    <IconCashBanknote />
                    Take payment
                  </Button>
                  <div className="flex w-full gap-2">
                    <Dialog>
                      <DialogTrigger
                        render={
                          <Button size="touch" variant="outline" className="flex-1">
                            <IconPrinter />
                            Drawer
                          </Button>
                        }
                      />
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Open the cash drawer?</DialogTitle>
                          <DialogDescription>
                            This is logged against your shift and shows up in the end-of-day count.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                          <DialogClose render={<Button variant="outline">Cancel</Button>} />
                          <DialogClose render={<Button>Open drawer</Button>} />
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <Button size="touch" variant="destructive" className="flex-1">
                      Void
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>

          {/* The back office: dense, read down a column, mouse and keyboard. */}
          <TabsContent value="office" className="space-y-4 pt-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <IconSearch className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="search" placeholder="Receipt number" className="w-56 pl-8" />
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="outlet">Outlet</Label>
                <Select defaultValue="All outlets">
                  <SelectTrigger id="outlet" className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Grouped for the padding, like every other select. No
                        `items` on the root here because these values are their
                        own labels, so there is nothing to map. */}
                    <SelectGroup>
                      <SelectItem value="All outlets">All outlets</SelectItem>
                      <SelectItem value="Gate 1">Gate 1</SelectItem>
                      <SelectItem value="Gate 2">Gate 2</SelectItem>
                      <SelectItem value="Cafe">Cafe</SelectItem>
                      <SelectItem value="Souvenirs">Souvenirs</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline">
                <IconRefresh />
                Sync now
              </Button>
              <Button variant="ghost">
                <IconReceipt />
                Export
              </Button>
            </div>

            <Card className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Receipt</TableHead>
                    <TableHead>Outlet</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {SALES.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell className="font-medium numeric">{sale.id}</TableCell>
                      <TableCell>{sale.outlet}</TableCell>
                      <TableCell>{sale.mode}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[sale.status as keyof typeof STATUS_VARIANT]}>
                          {sale.status}
                        </Badge>
                      </TableCell>
                      {/* Tabular figures: the column is meant to be scanned
                          downward during cash-up, so the digits must not jitter. */}
                      <TableCell className="text-right numeric">{rupees(sale.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4}>Counted</TableCell>
                    <TableCell className="text-right numeric">
                      {rupees(SALES.reduce((sum, sale) => sum + sale.total, 0))}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="palette" className="space-y-8 pt-6">
            <section className="space-y-3">
              <h2 className="text-sm font-medium">Greens</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="overflow-hidden rounded-xl border">
                  <div className="bg-primary p-6 text-primary-foreground">
                    <div className="font-semibold">Primary — preset</div>
                    <div className="text-sm numeric opacity-90">
                      oklch(0.527 0.154 150.069) · #008236
                    </div>
                    <div className="mt-2 text-sm">4.72:1 on its foreground — passes AA</div>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border">
                  <div className="p-6 text-white" style={{ backgroundColor: '#2EB872' }}>
                    <div className="font-semibold">Brand — as supplied</div>
                    <div className="text-sm numeric opacity-90">
                      oklch(0.694 0.155 155.70) · #2EB872
                    </div>
                    <div className="mt-2 text-sm">2.56:1 on white — fails AA</div>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-medium">Button variants</h2>
              <div className="flex flex-wrap items-center gap-2">
                <Button>Default</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="link">Link</Button>
                <Button disabled>Disabled</Button>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-medium">Sizes — registry, then ours</h2>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="xs">xs</Button>
                <Button size="sm">sm</Button>
                <Button size="default">default</Button>
                <Button size="lg">lg</Button>
                <Button size="touch">
                  <IconTicket />
                  touch
                </Button>
                <Button size="icon-touch" aria-label="icon-touch">
                  <IconTicket />
                </Button>
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-medium">Typeface</h2>
              <div className="grid gap-2">
                <p className="text-2xl">Geist — the quick brown fox jumps over the lazy dog</p>
                <p className="font-mono text-2xl numeric">
                  Geist Mono — ₹1,234,567.89 · 0123456789
                </p>
                <p className="text-sm text-muted-foreground">
                  Every row below has the same digit count, so any raggedness is the figures rather
                  than the numbers. Geist is strongly proportional by default — eight 1s measure
                  55.8px where eight 0s measure 107.5px. Under tabular-nums both are exactly 96px.
                </p>
                <div className="grid grid-cols-2 gap-8 text-lg">
                  <div>
                    <div className="mb-1 text-xs text-muted-foreground">
                      numeric — one width per digit
                    </div>
                    {[111111, 888888, 191101, 911999].map((amount) => (
                      <div key={amount} className="text-right numeric">
                        {rupees(amount)}
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="mb-1 text-xs text-muted-foreground">default figures</div>
                    {[111111, 888888, 191101, 911999].map((amount) => (
                      <div key={amount} className="text-right">
                        {rupees(amount)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
